import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Avatar,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { parseValidationErrorMessage, getLanguageLetter } from '../../../utils/Utilities';
import SnackBarAlert from '../../HelperComponents/SnackBarAlert';
import { useAuth } from '../../customContextProviders/AuthContext';
import { UpdateDeviceLanguage } from '../../../network/Language';

// ----------------------------------------------------------------------

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 400,
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 3,
  borderRadius: 1
};

const changeLanguageSchema = Yup.object().shape({
  language: Yup.string().required('Required')
});

NoxLanguageChangeModal.propTypes = {
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  _onAction: PropTypes.func,
  merchantData: PropTypes.object,
  languageData: PropTypes.object
};

export default function NoxLanguageChangeModal({
  opneAssignedModal,
  handleClose,
  _onAction,
  merchantData,
  languageData
}) {
  const { audioCubeAppLevelSettings } = useAuth();
  const [languageVal, setLanguageVal] = useState('');

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const getLanguageName = (shortCode) =>
    languageData?.data?.find((lang) => lang.shortCode === shortCode)?.language || shortCode;

  const changeLanguageFormik = useFormik({
    initialValues: {
      language: ''
    },
    validationSchema: changeLanguageSchema,
    onSubmit: () => {
      UpdateDeviceLanguage(merchantData?.deviceId, changeLanguageFormik?.values?.language)
        .then((res) => {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: `Language update from ${getLanguageName(
              merchantData?.language
            )} to ${getLanguageName(changeLanguageFormik.values.language)} initiated. Will reflect on the dashboard soon.`,
            severity: 'success'
          }));
          setIsSnackBarAlertOpen(true);

          handleClose();
        })
        .catch((err) => {
          setSnackBarMetas((prevState) => ({
            ...prevState,
            title: parseValidationErrorMessage(err),
            severity: 'error'
          }));
          setIsSnackBarAlertOpen(true);

          handleClose();
        })
        .finally(() => {
          changeLanguageFormik.setSubmitting(false);
          changeLanguageFormik.resetForm();
        });
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } =
    changeLanguageFormik;

  const handleModalClose = () => {
    handleClose();
    changeLanguageFormik.resetForm();
  };

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={handleModalClose}
        aria-labelledby="device-language-modal-title"
        aria-describedby="device-language-modal-description"
      >
        <FormikProvider value={changeLanguageFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={style}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3
                }}
              >
                <Typography
                  id="device-language-modal-title"
                  sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}
                >
                  Change Language
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LanguageOutlinedIcon />
                  <IconButton
                    data-testid="close-modal"
                    aria-label="Close change language modal"
                    onClick={handleModalClose}
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {languageData?.data ? (
                <FormControl fullWidth>
                  <InputLabel id="device-language">Language</InputLabel>
                  <Select
                    labelId="device-language"
                    id="device-language-select"
                    label="Language"
                    {...getFieldProps('language')}
                    error={Boolean(touched.language && errors.language)}
                    helperText={touched.language && errors.language}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250
                        }
                      }
                    }}
                  >
                    {Array.isArray(languageData?.data) &&
                      languageData.data.map(({ id, language, shortCode }) => (
                        <MenuItem key={id} value={shortCode}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{ bgcolor: '#fa7c05', width: 24, height: 24, fontSize: '1rem' }}
                            >
                              {getLanguageLetter(shortCode)}
                            </Avatar>
                            <Typography>{language}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              ) : null}

              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <LoadingButton
                  loading={changeLanguageFormik?.isSubmitting}
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={{ mt: 3, height: '38px' }}
                >
                  Change
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
